import { Role, rolesManipulator } from 'auth';
import dayjs from 'dayjs';
import get from 'lodash/get';
import { useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import { BadgeOnline } from '@/modules/common/components/items/MeetingItem.tsx';
import { AddUpdateMeetingModal } from '@/modules/common/components/modals/AddUpdateMeetingModal.tsx';
import { MeetingItemFullInfoModal } from '@/modules/common/components/modals/MeetingItemFullInfoModal.tsx';
import { Timezone } from '@/modules/common/constants/meeting-timezone.ts';
import { useGetMeetingsInfiniteQuery } from '@/modules/common/queries/meetings.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { Button, InfoCard, ModalProviderContext, Skeleton, getSelectValueByEnum } from 'ui';
import { PlusIcon } from 'ui/icons';
import { ScheduledMeetingListItem } from 'compliance-api';

const MeetingsSkeleton = () => {
  return (
    <div className="flex flex-col gap-8">
      {Array(5)
        .fill(null)
        .map((_, i) => (
          <div key={i} className="flex justify-between items-center">
            <div className="flex flex-col gap-2.5">
              <Skeleton width={91} height={13} />
              <Skeleton width={162} height={11} />
            </div>

            <Skeleton width={89} height={13} />
          </div>
        ))}
    </div>
  );
};

export const Meetings = () => {
  const userRole = rolesManipulator.current_user_role;
  const isAdmin = userRole === Role.COMPLIANCE_ADMIN;
  const { openModal } = useContext(ModalProviderContext);

  const { data: meetingsData, isLoading } = useGetMeetingsInfiniteQuery({
    page: 1,
    page_size: 15,
  });

  const meetings = get(meetingsData, 'pages', [])
    .flatMap(({ data }) => data)
    .slice(0, 5);

  const handleOpenCreateFormModal = useCallback(() => {
    openModal(AddUpdateMeetingModal);
  }, [openModal]);

  const handleOpenFullInfo = useCallback(
    (item: ScheduledMeetingListItem) => () => {
      openModal(MeetingItemFullInfoModal, {
        id: item.id,
      });
    },
    [openModal],
  );
  return (
    <InfoCard
      title="Meetings"
      loading={isLoading}
      classes={{ bottomAction: '!py-2' }}
      headerAction={
        isLoading ? (
          <Skeleton width={15} height={15} borderRadius={15} />
        ) : isAdmin ? (
          <button onClick={handleOpenCreateFormModal}>
            <img alt="add task" src={PlusIcon} />
          </button>
        ) : undefined
      }
      bottomAction={
        isLoading ? (
          <Skeleton width={52} height={11} />
        ) : (
          <Button className="!p-0 min-h-8" variant="plain">
            <Link
              to={{ pathname: adminDashboardPaths.static.meetings, search: location.search }}
              className="w-full h-full flex items-center justify-center"
            >
              View all
            </Link>
          </Button>
        )
      }
    >
      {isLoading && <MeetingsSkeleton />}
      {!isLoading && !meetings.length && (
        <div className="w-full h-full flex items-center justify-center py-2.5">
          You don't have meetings yet
        </div>
      )}
      {!isLoading && !!meetings.length && (
        <div className="flex flex-col gap-4">
          {meetings.map(meeting => (
            <div
              key={meeting.id}
              className="flex justify-between w-full items-center cursor-pointer hover:bg-gray-50"
              onClick={handleOpenFullInfo(meeting)}
            >
              <div className="flex flex-col gap-2">
                <div
                  className="text-sm leading-[22px] first-letter:capitalize break-words"
                  style={{ wordBreak: 'break-word' }}
                >
                  {meeting.subject}
                </div>
                <div className="text-[#737373] text-xs">
                  {`${dayjs(meeting.start_time).format('DD-MM-YYYY, HH:mm')} - ${dayjs(
                    meeting.end_time,
                  ).format('HH:mm')} (${
                    getSelectValueByEnum<Timezone>(meeting.time_zone, Timezone, false).label
                  })`}
                </div>
              </div>
              <div>
                <BadgeOnline isOnline={meeting.is_online_meeting} />
              </div>
            </div>
          ))}
        </div>
      )}
    </InfoCard>
  );
};
